<template>
  <section class="filters">
    <div class="input-container-wrap airports">
      <airports-dropdown
        :label="$t('destinationsSilo.flyingFrom')"
        :selected-item="airport"
        :items="filteredAirports"
        @change="onOriginSelect"
        @search="onOriginFilter"
      />
    </div>

    <div
      v-if="!!countries.length"
      class="input-container-wrap autocomplete"
    >
      <element-autocomplete
        :items="countries"
        :label="$t('destinationsSilo.showCountry')"
        :mobile-title="$t('destinationsSilo.showCountry')"
        :selected-item="selectedValue"
        field="countries"
        name="countries"
        same-window
        @change="onCountrySelect"
      />
    </div>

    <div
      v-if="!!labels.length"
      class="input-container-wrap checkboxes"
    >
      <checkboxes
        :header-title="$t('destinationsSilo.filters')"
        :labels="labels"
        :selected="selectedLabel"
        @update="onLabelsUpdate"
      />
    </div>

    <div class="bottom-row">
      <div>
        <a
          v-if="areFiltersUsed"
          href="javascript:void(0);"
          class="reset-filters"
          @click="resetFilters"
        >{{ $t('destinationsSilo.resetFilters') }}</a>
      </div>

      <blue-button
        :label="$t('buttons.submit')"
        @clicked="submitFilters"
      />
    </div>
  </section>
</template>

<script>
  import { debounce } from 'lodash-es'
  import { mapState } from 'vuex'
  import AirportsDropdown from 'components/eHorizon/Destination/AirportsDropdown'
  import ElementAutocomplete from 'components/Elements/Autocomplete'
  import Checkboxes from 'components/DestinationsTemplate/Checkboxes'

  export default {
    components: {
      AirportsDropdown,
      ElementAutocomplete,
      Checkboxes,
      BlueButton: () => import(/* webpackChunkName: "btn-blue" */ 'components/Buttons/Blue'),
    },
    computed: {
      ...mapState({
        airport: (state) => state.destinationsSilo.airport,
        selectedCountry: (state) => state.destinationsSilo.selectedCountry,
        selectedLabel: (state) => state.destinationsSilo.selectedLabel,
        countryFromUrl: (state) => state.destinationsSilo.countryFromUrl
      }),

      airports() {
        return this.$store.getters['destinationsSilo/airports']
      },
      labels() {
        return this.$store.getters['destinationsSilo/labels']
      },
      countries() {
        return this.$store.getters['destinationsSilo/countries']
      },
      selectedValue() {
        if (this.countryFromUrl === '') {
          return this.selectedCountry
        }

        const selected = this.countries.find(({ value }) => value === this.countryFromUrl)

        return !selected || !Object.keys(selected)?.length
          ? this.selectedCountry
          : selected
      },
      filteredAirports() {
        const searchResults = this.$t('searchForm.originDestin.origin.matchedOrigins')

        if (!this.airports.includes(searchResults)) {
          return this.airports
        }

        return this.airports.filter((item) => item !== searchResults)
      },
      areFiltersUsed() {
        return (
          this.airport?.code !== 'RIX' ||
          !!this.selectedCountry?.value ||
          !!this.selectedLabel
        )
      }
    },

    methods: {
      onOriginSelect(value) {
        this.$store.commit('destinationsSilo/changeAirport', value)
        this.$store.commit('destinationsSilo/setCountryFromUrl', '')
        this.$store.commit('destinationsSilo/selectDestination', null)
        this.doFetch()
      },
      onCountrySelect(countryValue) {
        this.$store.dispatch('destinationsSilo/setSelectedCountry', countryValue)
        this.$store.commit('destinationsSilo/setCountryFromUrl', '')
        this.$store.commit('destinationsSilo/selectDestination', null)
      },
      onLabelsUpdate(selectedLabel) {
        this.$store.commit('destinationsSilo/setSelectedLabel', selectedLabel)
        this.$store.commit('destinationsSilo/selectDestination', null)
      },
      onOriginFilter: debounce(function (value) {
        this.$store.commit('destinationsSilo/setFilter', value)
      }, 0),
      doFetch() {
        this.$store.commit('main/showPlaneLoader', true)

        this.$store
          .dispatch('destinationsSilo/fetchDestinations', true)
          .then(() => this.$store.commit('main/showPlaneLoader', false))
          .catch(() => this.$store.commit('main/showPlaneLoader', false))
      },
      resetFilters() {
        this.$store.dispatch('destinationsSilo/clearFilters')
        this.$store.dispatch('destinationsSilo/resetOrigins')
        this.$store.commit('destinationsSilo/setCountryFromUrl', '')
        this.doFetch()
      },

      submitFilters () {
        this.$store.dispatch('main/closeModal')
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
>
  @import '~assets/styles/common.scss';

  .content-wrap {
    display: block;

    @include bp-mf-tablet {
      display: grid;
      grid-template-columns: 3fr 9fr;
      column-gap: 0;
    }
  }

  .title-wrap {
    display: block;

    @include bp-mf-tablet {
      display: grid;
      column-gap: 20px;
      grid-template-columns: 9fr 3fr;
    }
  }

  .selector {
    &-wrap {
      align-self: flex-end;
      display: flex;
      justify-content: space-between;
      margin-top: 16px;

      @include bp-mf-tablet {
        display: block;
        margin-top: 0;
      }

      .filters-trigger {
        align-items: center;
        border-radius: 4px;
        border: 1px solid $grey-border;
        color: $blue-medium;
        cursor: pointer;
        display: flex;
        font-size: rem(16, 16);
        height: 44px;
        justify-content: center;
        padding: 10px 33px;

        @include bp-mf-tablet {
          display: none;
        }
      }
    }

    &-text {
      display: none;

      @include bp-mf-tablet {
        width: 100%;
        display: block;
      }
    }

    &-tab {
      display: flex;
      align-items: center;

      .item {
        align-items: center;
        border: 1px solid $blue-medium;
        color: $blue-medium;
        cursor: pointer;
        display: flex;
        font-size: rem(16, 16);
        height: rem(44, 16);
        padding: 10px;
        text-align: center;
        width: auto;

        @include bp-mf-tablet {
          padding: 14px 17px;
        }
      }

      .map {
        border-radius: 4px 0 0 4px;
      }

      .list {
        border-radius: 0 4px 4px 0;
      }

      .icon {
        height: 24px;
        width: 24px;

        @include bp-mf-tablet {
          margin-right: 13px;
        }
      }

      .active {
        background: $blue-medium;
        color: white;

        .icon {
          /deep/ span {
            background: white;
          }
        }
      }
    }
  }

  .airports {
    margin-bottom: 16px !important;
  }

  .reset-filters {
    color: $blue-medium;
    line-height: 52px;
    font-weight: 500;
    text-decoration: none;

    @include bp-mf-desktop {
      line-height: inherit;
    }
  }

  .autocomplete /deep/ {
    .multiselect__content-wrapper {
      margin-bottom: 0 !important;
    }

    .multiselect--active {
      .arrow {
        display: none;
      }
    }

    .arrow {
      transform: rotate(0deg);

      &::after {
        content: $filled-icon-caret;
        @include filled-icon-font();
        font-size: 16px;
        left: calc(50% - 8px);
        top: calc(50% - 8px);
      }
    }

    .selected {
      font-weight: 700;
    }
  }

  .filters {
    @include bp-mf-tablet {
      padding: 24px 0;
      min-height: 380px;
    }

    @include bp-mf-desktop {
      padding: 0;
      min-height: unset;
    }

    /deep/ .multiselect {
      &__content {
        padding-left: 0 !important;
      }

      &__element {
        max-width: 100% !important;
      }

      &__option {
        max-width: 100% !important;
        padding-left: 16px !important;
      }

      &__content-wrapper {
        margin-bottom: 0 !important;
        max-height: 275px !important;

        @include bp-mf-tablet {
          max-height: 210px !important;
        }

        @include bp-mf-desktop {
          max-height: 300px !important;
        }
      }
    }
  }

  .bottom-row {
    display: flex;
    justify-content: space-between;

    .btn {
      min-width: 144px;

      @include bp-mf-desktop {
        display: none;
      }
    }
  }
</style>
